import { BaseService } from './BaseService';
import { ApiResponse, InformeResponse, InformeRequest } from '@/types';

/**
 * Servicio para gestionar informes de jugadores
 */
export class InformeService extends BaseService {
  constructor() {
    super('/informes');
  }

  /**
   * Crea un nuevo informe con posible archivo de foto
   * @param data - Datos del informe (InformeRequest o FormData)
   * @returns Promise con el informe creado
   */
  async crearInforme(data: InformeRequest | FormData): Promise<ApiResponse<InformeResponse>> {
    const isFormData = data instanceof FormData;
    
    const response = await fetch(`${this.baseURL}${this.endpoint}`, {
      method: 'POST',
      headers: isFormData ? this.getHeadersForFormData() : this.getHeaders(),
      body: isFormData ? data : JSON.stringify(data)
    });
    
    return this.handleResponse<ApiResponse<InformeResponse>>(response);
  }

  /**
   * Obtiene los headers para FormData (sin Content-Type)
   */
  private getHeadersForFormData(): HeadersInit {
    const token = localStorage.getItem('auth_token');
    return {
      ...(token && { Authorization: `Bearer ${token}` })
      // No incluir Content-Type para FormData, el navegador lo establece automáticamente
    };
  }

  /**
   * Obtiene informes paginados con filtros
   */
  async getInformesPaginados(params: {
    page: number;
    size: number;
    autorId?: number;
    search?: string;
    categoria?: string;
    categoriaFutura?: string;
    nivel?: string;
    posicion?: string;
    año?: number;
    partidoId?: number;
    dorsal?: number;
    lateralidad?: string;
    sortBy?: string;
    sortDir?: string;
  }): Promise<ApiResponse<{
    informes: InformeResponse[];
    totalPages: number;
    totalElements: number;
    last: boolean;
  }>> {
    return this.get('/todos', params);
  }

  /**
   * Obtiene informes por partido
   * @param partidoId - ID del partido
   * @returns Promise con los informes del partido
   */
  async getInformesByPartido(partidoId: number): Promise<ApiResponse<InformeResponse[]>> {
    const response = await fetch(`${this.baseURL}${this.endpoint}/partido/${partidoId}`, {
      method: 'GET',
      headers: this.getHeaders()
    });
    return this.handleResponse<ApiResponse<InformeResponse[]>>(response);
  }

  /**
   * Obtiene informes por entrenador
   * @param entrenadorId - ID del entrenador
   * @returns Promise con los informes del entrenador
   */
  async getInformesByEntrenador(entrenadorId: number): Promise<ApiResponse<InformeResponse[]>> {
    const response = await fetch(`${this.baseURL}${this.endpoint}/entrenador/${entrenadorId}`, {
      method: 'GET',
      headers: this.getHeaders()
    });
    return this.handleResponse<ApiResponse<InformeResponse[]>>(response);
  }

  /**
   * Obtiene informes por partido y entrenador
   * @param partidoId - ID del partido
   * @param entrenadorId - ID del entrenador
   * @returns Promise con los informes del partido filtrados por entrenador
   */
  async getInformesByPartidoYEntrenador(partidoId: number, entrenadorId: number): Promise<ApiResponse<InformeResponse[]>> {
    const response = await fetch(`${this.baseURL}${this.endpoint}/partido/${partidoId}/entrenador/${entrenadorId}`, {
      method: 'GET',
      headers: this.getHeaders()
    });
    return this.handleResponse<ApiResponse<InformeResponse[]>>(response);
  }

  /**
   * Actualiza un informe existente
   * @param id - ID del informe
   * @param data - Datos actualizados del informe (InformeRequest o FormData)
   * @returns Promise con el informe actualizado
   */
  async actualizarInforme(id: number, data: InformeRequest | FormData): Promise<ApiResponse<InformeResponse>> {
    const isFormData = data instanceof FormData;
    
    const response = await fetch(`${this.baseURL}${this.endpoint}/${id}`, {
      method: 'PUT',
      headers: isFormData ? this.getHeadersForFormData() : this.getHeaders(),
      body: isFormData ? data : JSON.stringify(data)
    });
    
    return this.handleResponse<ApiResponse<InformeResponse>>(response);
  }

  /**
   * Elimina un informe
   * @param id - ID del informe a eliminar
   * @returns Promise con la respuesta de eliminación
   */
  async eliminarInforme(id: number): Promise<ApiResponse<void>> {
    return this.delete(id);
  }

  /**
   * Obtiene la URL de la foto de un informe
   * @param id - ID del informe
   * @returns URL de la foto
   */
  obtenerUrlFoto(id: number): string {
    return `${this.baseURL}${this.endpoint}/${id}/foto`;
  }

  /**
   * Obtiene la foto de un informe como blob con autenticación
   * @param id - ID del informe
   * @returns Promise con la URL del blob o null si hay error
   */
  async obtenerFotoBlob(id: number): Promise<string | null> {
    const token = localStorage.getItem('auth_token');
    if (!token) return null;

    try {
      const response = await fetch(`${this.baseURL}${this.endpoint}/${id}/foto`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('Error obteniendo foto:', error);
      return null;
    }
  }
}

// Exportar instancia singleton
export const informeService = new InformeService();